<template lang="pug">
#inspection-reservation-list(v-cloak)
  .box
    .box-header
      .d-flex.justify-content-start.align-items-center
        .d-flex.justify-content-start.flex-column
          .title
            span 施設見学一覧
        .d-flex.justify-content-end.align-items-start.align-self-stretch.flex-fill
          button.btn.register-btn(type="button" @click="gotoRegister") 代理登録
      .error-messages
        .error-message {{ errorMessage }}

    .box-body
      b-tabs(v-model="tabIndex")
        // futures and notaccepted
        b-tab.tab
          template(v-slot:title)
            .tab-title
              span 未受付・予定
          FuturesAndNotAcceptedTab(ref="futuresAndNotAcceptedTab" @onChangeApproved="onChangeApproved" @onEdit="onEdit" @onDelete="onDelete")
        // histories
        b-tab.tab
          template(v-slot:title)
            .tab-title
              span 過去の履歴
          HistoriesTab(ref="historiesTab" @onChangeApproved="onChangeApproved" @onEdit="onEdit" @onDelete="onDelete")

  OkCancelModal(v-model="showDeleteConfirmModal" title="施設見学を削除しますか？" @ok="deleteInspectionReservation(inspectionReservationsSortKey)")
  OkModal(v-model="showDeleteSuccessModal" title="削除に成功しました。")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Ref, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";

import InspectionReservationsService, { InspectionReservationsConsentedPatchModel } from "@/commons/services/InspectionReservations";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import FuturesAndNotAcceptedTab from "@/pc/containers/inspectionReservations/FuturesAndNotAcceptedTab.vue";
import HistoriesTab from "@/pc/containers/inspectionReservations/HistoriesTab.vue";
import { InspectionReservationTableItem } from "@/pc/containers/inspectionReservations/InspectionReservationTable.vue";

/**
 * ### 施設見学一覧画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkCancelModal,
    OkModal,
    FuturesAndNotAcceptedTab,
    HistoriesTab
  }
})
export default class InspectionReservationList extends Vue {
  /** 施設見学サービス */
  @Inject("InspectionReservationsService") inspectionReservationsService!: InspectionReservationsService;

  /** 未受付・予定タブ */
  @Ref() futuresAndNotAcceptedTab!: FuturesAndNotAcceptedTab;
  /** 過去の履歴タブ */
  @Ref() historiesTab!: HistoriesTab;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** エラーメッセージ */
  errorMessage: string = "";
  /** 現在表示しているタブ番号 */
  tabIndex: number = 0;

  /** 削除確認モーダル表示可否 */
  showDeleteConfirmModal: boolean = false;
  /** 削除完了モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;
  /** 削除対象の施設見学申込み日 */
  inspectionReservationsSortKey: string | null = null;

  public refreshTabs(): void {
    this.futuresAndNotAcceptedTab.refresh();
    this.historiesTab.refresh();
    // サイドメニューを更新
    this.$emit("refresh");
    return;
  }

  /**
   * ### 受付状態を更新する
   */
  public async onChangeApproved(item: InspectionReservationTableItem): Promise<void> {
    try {
      this.isBusy = true;
      this.loadingMessage = "更新中...";

      const requestBody: InspectionReservationsConsentedPatchModel = this.createChangeApprovedRequestBody(item);
      await this.inspectionReservationsService.patch(userStore, item.inspectionReservationsSortKey, requestBody);

      this.refreshTabs();
    } catch (error) {
      const systemError: SystemError = error as SystemError;

      if (systemError.statusCode == 409) {
        this.refreshTabs();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 受付状態の変更リクエストボディを作成する
   */
  public createChangeApprovedRequestBody(item: InspectionReservationTableItem): InspectionReservationsConsentedPatchModel {
    return {
      approved: item.approved, // 受付状態は子コンポーネントで値更新しているのでそのままAPI呼び出しして更新する
      datetime: item.datetime,
      customerName: item.customerName,
      receptionMemo: item.receptionMemo,
      updated: item.updated
    };
  }

  /**
   * ### 代理登録画面へ遷移する
   */
  public gotoRegister(): void {
    this.$router.push(PcUrl.InspectionReservationRegister);
    return;
  }

  /**
   * ### 施設見学編集画面へ遷移する
   */
  public onEdit(item: InspectionReservationTableItem): void {
    this.$router.push({ name: PcUrl.InspectionReservationEdit, params: { datetime: `${item.inspectionReservationsSortKey}` } });
    return;
  }

  /**
   * ### 施設見学予約の削除確認モーダルを表示する
   */
  public onDelete(item: InspectionReservationTableItem): void {
    this.inspectionReservationsSortKey = item.inspectionReservationsSortKey;
    this.showDeleteConfirmModal = true;
    return;
  }

  /**
   * ### 施設見学予約を削除する
   */
  public async deleteInspectionReservation(inspectionReservationsSortKey: string): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    await this.inspectionReservationsService.delete(userStore, inspectionReservationsSortKey);

    this.showDeleteSuccessModal = true;
    this.isBusy = false;

    this.refreshTabs();

    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

#inspection-reservation-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    .title {
      font-size: 22px;
      font-weight: bold;
    }
  }
}
</style>
